import React, { useEffect, useState } from "react";
import { Form } from "semantic-ui-react";
import { useFormik } from "formik";
import { map } from "lodash";
import { AcademicCycle, Syllabu, Level } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./AcademicCycleForm.form";
import { withEmptyOption } from "../../../../utils";
import "./AcademicCycleForm.scss";

const levelController = new Level();
const academicCycleController = new AcademicCycle();
const syllabuController = new Syllabu();

export function AcademicCycleForm(props) {
  const { onClose, onReload, academicCycle } = props;
  const { accessToken } = useAuth();

  const [levelsOptions, setLevelsOptions] = useState([]);
  const [syllabusOptions, setSyllabusOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setSyllabusOptions([]);
        const response = await syllabuController.getSyllabus(accessToken);
        const options = response.map((syllabu) => ({
          value: syllabu.id,
          text: syllabu.name,
        }));
        setSyllabusOptions(options);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [academicCycle, accessToken]);

  const parseDate = (fecha) => {
    return fecha ? new Date(fecha).toISOString().split("T")[0] : "";
  };

  const formik = useFormik({
    initialValues: initialValues(academicCycle),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = {
          levelId: formValue.levelId,
          name: formValue.name,
          startDate: formValue.startDate,
          endDate: formValue.endDate,
          syllabuId: formValue.syllabuId,
          pedagogicalHour: parseInt(formValue.pedagogicalHour, 10),
          shiftStart: `${formValue.shiftStart}:00`,
          shiftEnd: `${formValue.shiftEnd}:00`,
          recessStart: `${formValue.recessStart}:00`,
          recessEnd: `${formValue.recessEnd}:00`,
          periodType: formValue.periodType,
        };

        if (academicCycle) {
          delete data.syllabuId;
          await academicCycleController.updateAcademicCycle(
            academicCycle.id,
            data
          );
        } else {
          data.status = 1;
          await academicCycleController.createAcademicCycle(data);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        setLevelsOptions([]);
        const response = await levelController.getLevels(accessToken);
        const options = response.map((level) => ({
          value: level.id,
          text: level.name,
        }));
        setLevelsOptions(options);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [accessToken]);

  // Generate Year Options
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 9; i <= currentYear + 1; i++) {
    yearOptions.push({
      key: i,
      text: `${i}`,
      value: `${i}`,
    });
  }
  // Sort descending to show newest first? Or ascending? Usually newest is more relevant.
  // The request said "intervalo de 11 años... 9 anteriores ... 1 posterior".
  // Let's keep them in ascending or descending? The loop is ascending.
  // I'll reverse it to show future/current at top? No, usually Selects are handled by Semantic UI nicely.
  // Let's just keep the loop order (2015, ..., 2025, 2026).

  const periodTypeOptions = [
    { key: "bimestre", text: "Bimestre", value: "bimestre" },
    { key: "trimestre", text: "Trimestre", value: "trimestre" },
  ];

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="academic-cycle-row">
        <Form.Select
          className="edunova-select"
          name="name"
          label="Nombre de ciclo académico"
          placeholder="Seleccionar año"
          clearable
          options={withEmptyOption(yearOptions, "Seleccionar año")}
          onChange={(e, { value }) => formik.setFieldValue("name", value)}
          value={formik.values.name}
          error={formik.errors.name}
        />
        <Form.Select
          className="edunova-select"
          name="periodType"
          label="Tipo de período"
          placeholder="Seleccionar tipo de período"
          clearable
          options={withEmptyOption(periodTypeOptions, "Seleccionar tipo de período")}
          onChange={(e, { value }) => formik.setFieldValue("periodType", value)}
          value={formik.values.periodType}
          error={formik.errors.periodType}
        />
      </div>
      <div className="academic-cycle-row">
        <Form.Select
          className="edunova-select"
          name="levelId"
          label="Nivel académico"
          placeholder="Selecciona un nivel académico"
          clearable
          options={withEmptyOption(levelsOptions, "Selecciona un nivel académico")}
          onChange={(e, { value }) => formik.setFieldValue("levelId", value)}
          value={formik.values.levelId}
          error={formik.errors.levelId}
          disabled={!!academicCycle}
        />
        <Form.Select
          className="edunova-select"
          name="syllabuId"
          label="Malla curricular"
          placeholder="Selecciona una malla curricular"
          clearable
          options={withEmptyOption(syllabusOptions, "Selecciona una malla curricular")}
          onChange={(e, { value }) => formik.setFieldValue("syllabuId", value)}
          value={formik.values.syllabuId}
          error={formik.errors.syllabuId}
          disabled={!!academicCycle}
        />
      </div>
      <div className="academic-cycle-row">
        <Form.Input
          name="startDate"
          type="date"
          fluid
          label="Fecha de inicio"
          onChange={formik.handleChange}
          value={parseDate(formik.values.startDate)}
          error={formik.errors.startDate}
        />
        <Form.Input
          name="endDate"
          type="date"
          fluid
          label="Fecha de fin"
          onChange={formik.handleChange}
          value={parseDate(formik.values.endDate)}
          error={formik.errors.endDate}
        />
      </div>

      <h4 style={{ margin: "20px 0 10px 0", borderBottom: "1px solid #ccc", paddingBottom: "5px" }}>
        Configuración de Horario Pedagógico
      </h4>

      <div className="academic-cycle-row">
        <Form.Input
          name="pedagogicalHour"
          type="number"
          label="Hora Pedagógica (minutos)"
          placeholder="Ej. 45 o 50"
          onChange={formik.handleChange}
          value={formik.values.pedagogicalHour}
          error={formik.errors.pedagogicalHour}
        />
        <div style={{ flex: "1 1 auto", maxWidth: "calc(50% - 0.5rem)" }} />
      </div>

      <div className="academic-cycle-row">
        <Form.Input
          name="shiftStart"
          type="time"
          label="Inicio del Turno"
          onChange={formik.handleChange}
          value={formik.values.shiftStart}
          error={formik.errors.shiftStart}
        />
        <Form.Input
          name="shiftEnd"
          type="time"
          label="Fin del Turno"
          onChange={formik.handleChange}
          value={formik.values.shiftEnd}
          error={formik.errors.shiftEnd}
        />
      </div>

      <div className="academic-cycle-row">
        <Form.Input
          name="recessStart"
          type="time"
          label="Inicio del Receso"
          onChange={formik.handleChange}
          value={formik.values.recessStart}
          error={formik.errors.recessStart}
        />
        <Form.Input
          name="recessEnd"
          type="time"
          label="Fin del Receso"
          onChange={formik.handleChange}
          value={formik.values.recessEnd}
          error={formik.errors.recessEnd}
        />
      </div>

      <Form.Button type="submit" primary fluid loading={formik.isSubmitting} style={{ marginTop: "15px" }}>
        {academicCycle ? "Actualizar ciclo académico" : "Crear ciclo académico"}
      </Form.Button>
    </Form>
  );
}
